import { ReferenceLookupService } from '@/services';
import {
  BaseDocumentSearchModel,
  CabinetModel,
  DocumentsToStore,
  ExistingDocumentOption,
  PagesStoreMode,
  ReferenceLookupMultiMatchOption,
  ReferenceLookupNoMatchOption,
  ReferenceLookupResultModel,
  ReferenceLookupResultType,
  ReferenceLookupSearchModel,
  SchemaModel,
  TargetDocumentTypeSettingsModel,
  MultiValueReferenceModel,
  MultiValueConstants
} from '@/types';
import { ArrayUtils } from '@/utils';
import { mapActions, mapGetters, mapState } from 'vuex';
import { BvTableFieldArray } from 'bootstrap-vue';

export const StoreDocumentHelpers = {
  data() {
    return {
      isPageRangeIsValid: false,
      isSubmitLoading: false,
      isPageLoading: true
    };
  },
  methods: {
    ...mapActions('documentViewStore', ['loadPage', 'setDocumentSearchModel', 'unloadDocument']),
    ...mapActions({
      setReadonly(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setReadonly`, payload);
      },
      setSchemaValuesFromReferences(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setSchemaValuesFromReferences`, payload);
      },
      setDocumentDateValue(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setDocumentDateValue`, payload);
      },
      setDynamicFormSchema(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setDynamicFormSchema`, payload);
      },
      setMultiValueReferenceValues(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setMultiValueReferenceValues`, payload);
      },
      clearMultiValueReferenceValues(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/clearMultiValueReferenceValues`);
      },
      setSchemaValuesFromRetainedFields(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/setSchemaValuesFromRetainedFields`, payload);
      },
      clearExistingDocument(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/clearExistingDocument`, payload);
      },
      selectExistingDocument(dispatch, payload) {
        return dispatch(`${this.storeNameSpace}/selectExistingDocument`, payload);
      }
    }),
    loadDocumentPreview(this: any) {
      this.unloadDocument();
      this.setDocumentSearchModel({
        cabinetId: this.sourceCabinetId,
        documentId: this.sourceDocumentId,
        documentTypeId: this.sourceDocumentTypeId
      } as BaseDocumentSearchModel);
      this.loadPage(this.pageViewedBeforeAddToExisting);
    },
    async performSpecialLookup(this: any) {
      try {
        const searchModel: ReferenceLookupSearchModel = {
          references: this.referenceValues,
          documentDate: this.documentDateReferenceValue,
          multiValueReferences: null
        };
        const result: ReferenceLookupResultModel = await ReferenceLookupService.getSpecialLookupReferencesAsync(this.targetDocumentTypeId, searchModel);

        const setSchemaValuesFromResult = () => {
          this.setReadonly(true);
          this.setSchemaValuesFromReferences(result.references);
          this.setMultiValueReferenceValues(result.multiValueReferences);
          if (result.applyDocumentDate) {
            this.setDocumentDateValue(result.documentDate);
          }
        };

        switch (result.resultType) {
          case ReferenceLookupResultType.MatchFound:
            setSchemaValuesFromResult();
            break;

          case ReferenceLookupResultType.MultipleMatchesFound:
            if (result.multiMatchOption === ReferenceLookupMultiMatchOption.UseFirstMatch) {
              setSchemaValuesFromResult();
            }
            break;

          case ReferenceLookupResultType.NoMatchFound:
            if (result.noMatchOption === ReferenceLookupNoMatchOption.ClearReferences) {
              this.setReadonly(true);
              this.resetToOriginalReferenceValues();
            }
            break;
        }
      } catch (error) {
        this.$log('Failed to get special lookup references');
      } finally {
        this.setReadonly(false);
      }
    },
    async onFieldUpdated(this: any, schema: SchemaModel) {
      if (this.isSpecialLookupField(schema.id)) {
        this.$nextTick(() => {
          this.performSpecialLookup();
        });
      }
    },
    updateSchemaReferences(this: any, newSchemaReferences: SchemaModel[]) {
      this.schemas = newSchemaReferences.map((model: SchemaModel) => {
        return { ...model };
      });
      this.setSchemaValuesFromReferences(newSchemaReferences);
      this.setDocumentDateValue(this.initialDocumentDateValue);
    },
    clearReferenceValues(this: any) {
      const clearedReferences = this.schemas.map((field: SchemaModel) => {
        return { ...field, value: '' };
      });

      this.updateSchemaReferences(clearedReferences);
      this.clearMultiValueReferenceValues();
    },
    resetToOriginalReferenceValues(this: any) {
      this.updateSchemaReferences(this.initialReferenceValues);
      this.setMultiValueReferenceValues(this.initialMultiValueReferenceValues);
    },
    getRetainedReferences(this: any) {
      this.retainedReferences = [] as SchemaModel[];
      if (this.retainReferences) {
        this.retainedReferences = this.referenceFields;
      }
    },
    applyRetainedReferences(this: any) {
      if (this.retainedReferences?.length > 0 && !this.clearGridAfterUpdate) {
        this.setSchemaValuesFromRetainedFields(this.retainedReferences);
      }
    },
    initialisePageSelectionValues(this: any) {
      this.selectedPages = '1';
      this.pagesStoreMode = PagesStoreMode.NoSplitting;
    },
    onAddToExisting(this: any) {
      if (this.isPageLoading || this.documentDetailsReadonly) {
        return;
      }
      if (this.existingDocumentSet) {
        this.clearExistingDocument();
      } else {
        this.onAddToExistingNavigation = true;
        this.selectExistingDocument(this.currentPage);
      }
    },
    isComponentAlive(this: any) {
      return !this._isDestroyed;
    },
    getMissingMultiValueReferenceWithDefaultValues(this: any, multiValueReferenceItem: any) {
      const fieldsFromDataRow = Object.keys(multiValueReferenceItem).filter((key) => key !== MultiValueConstants.RowIdAlias);
      const fieldsFromDynamicFormSchema = this.multiValueDynamicFormSchema.map((schema: SchemaModel) => schema.id);
      const missingFields = fieldsFromDynamicFormSchema.filter((field: string) => !fieldsFromDataRow.includes(field));
      return Object.fromEntries(missingFields.map((field: string) => [field, '']));
    }
  },
  computed: {
    ...mapGetters('documentViewStore', ['document', 'hasDocumentLoaded', 'isPageSplittingSupported']),
    ...mapState({
      documentToStoreView(state: any, getters: any) {
        return getters[`${this.storeNameSpace}/documentToStoreView`];
      },
      targetDocumentTypeId(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/targetDocumentTypeId`];
      },
      existingDocumentSet(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/existingDocumentSet`];
      },
      clearGridAfterUpdate(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/clearGridAfterUpdate`];
      },
      retainReferences(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/retainReferences`];
      },
      isSpecialLookupField(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/isSpecialLookupField`];
      },
      initialMultiValueReferenceValues(state: any, getters: any) {
        return getters[`${(this as any).storeNameSpace}/initialMultiValueReferenceValues`];
      }
    }),
    storeNameSpace(this: any) {
      return this.$route.name === DocumentsToStore.DocumentsToStore.name ? 'documentToStoreStore' : 'editReferencesStore';
    },
    schemas: {
      get(this: any): SchemaModel[] {
        return this.documentToStoreView.dynamicFormSchema;
      },
      set(this: any, value: SchemaModel[]) {
        if (value?.length > 0) {
          this.setDynamicFormSchema(value);
        }
      }
    },
    multiValueDynamicFormSchema(this: any): SchemaModel[] {
      return this.documentToStoreView.multiValueDynamicFormSchema;
    },
    multiValueReferenceItems: {
      get(this: any) {
        return this.documentToStoreView.multiValueReferences ?? [];
      },
      set(this: any, value: any) {
        this.setMultiValueReferenceValues(value);
      }
    },
    multiValueReferences(this: any): MultiValueReferenceModel[] {
      const schemaKeys = this.multiValueDynamicFormSchema.map((schema: SchemaModel) => schema.id);
      return this.multiValueReferenceItems
        .map((dataRow: Record<string, any>) => ({
          id: dataRow[MultiValueConstants.RowIdAlias] as string,
          values: {
            ...Object.fromEntries(Object.entries(dataRow).filter(([key]) => key !== MultiValueConstants.RowIdAlias && schemaKeys.includes(key))),
            ...this.getMissingMultiValueReferenceWithDefaultValues(dataRow)
          }
        }))
        .filter((multiValueReference: { id: string; values: Record<string, any> }) => Object.keys(multiValueReference.values).length !== 0);
    },
    multiValueReferenceFields(this: any): BvTableFieldArray {
      return this.documentToStoreView.multiValueReferenceColumns as BvTableFieldArray;
    },
    targetCabinets: {
      get(this: any): CabinetModel[] {
        return this.documentToStoreView.cabinets;
      }
    },
    targetDocumentTypeSettings: {
      get(this: any): TargetDocumentTypeSettingsModel {
        return this.documentToStoreView.targetDocumentTypeSettings;
      }
    },
    addToExistingButtonText(this: any): string {
      if (!this.existingDocumentSet) {
        return 'Add to existing document';
      }

      switch (this.existingDocumentOption) {
        case ExistingDocumentOption.ReplaceDocument:
          return 'Will replace the existing document';
        case ExistingDocumentOption.CreateHistoricVersion:
          return 'Will add as an historic version';
        case ExistingDocumentOption.AddToDocument:
        default:
          return 'Will be added to existing document';
      }
    },
    isSelectedPageRangeValid(this: any): boolean {
      return (this.isPageRangeIsValid && this.selectedPages) || this.document.viewConversionId || this.document.hasNotification;
    },
    haveTargetCabinets(this: any): boolean {
      return this.targetCabinets?.length > 0;
    },
    selectedPageRange(this: any): number[] | undefined {
      if (this.isPageRangeIsValid) {
        return ArrayUtils.getPageNumbersArray(this.selectedPages);
      }
    },
    showThumbnail(this: any): boolean {
      return this.document.totalPages > 0 && !this.document.hasNotification && !this.document.viewConversionId;
    },
    allPagesAreSelectedForStore(this: any): boolean {
      const pages = this.document.viewConversionId || this.document.hasNotification ? null : this.selectedPages;
      return pages == null || ArrayUtils.getPageNumbersArray(this.selectedPages).length === this.document.totalPages;
    },
    documentViewerDeleteIsDisabled(this: any): boolean {
      return this.isSubmitLoading;
    },
    selectedPageRangeIsAllPages(this: any): boolean {
      return this.selectedPageRange?.length && this.document?.totalPages && this.selectedPageRange.length === this.document.totalPages;
    },
    showStoreOptions(this: any): boolean {
      return this.hasDocumentLoaded && this.isPageSplittingSupported && this.pagesStoreMode;
    },
    showDocumentCannotBeSplit(this: any): boolean {
      return this.hasDocumentLoaded && !this.isPageLoading && !this.isPageSplittingSupported && this.document.totalPages > 1;
    }
  }
};
